import React, { useState, useRef, useEffect } from 'react';
import cn from 'classnames';

import PieChart from '../../common/features/PieChart';
import AlertIcon from '../../../assets/icons/common/AlertIcon';
import RescanIcon from '../../../assets/icons/device/RescanIcon';
import RefreshIcon from '../../../assets/icons/device/RefreshIcon';
import {
  useDeviceSignalScanMutation,
  useDeviceRestartConnectionMutation,
  useLazyGetListDevicesBySnQuery,
} from '../../../redux/devicesApi';

import {
  RESCAN_TOOLTIP,
  REFRESH_TOOLTIP,
  RECONNECT_DESCRIPTION,
  RECONNECT_TIMEOUT_DESCRIPTION,
} from '../../../constants/deviceStatus';
import styles from './styles.module.scss';

const RECONNECT_TIMEOUT = 45;
const TIMER_INTERVAL = 1000;
const STATUS_CHECK_INTERVAL = 15000;

const DeviceStatus = ({
  isExpired,
  connectionStatusName,
  className,
  signalLevel,
  deviceSn,
  expiredAsOffline = false,
  showPieChart = false,
  refetch,
}) => {
  const [deviceSignalScan] = useDeviceSignalScanMutation();
  const [deviceRestartConnection] = useDeviceRestartConnectionMutation();
  const [getDeviceStatus] = useLazyGetListDevicesBySnQuery();
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [timer, setTimer] = useState(RECONNECT_TIMEOUT);
  const [reconnectProgress, setReconnectProgress] = useState(0);
  const timerIntervalRef = useRef(null);
  const checkIntervalRef = useRef(null);

  const isOnline = connectionStatusName?.toLowerCase() === 'online';
  const isConnecting = connectionStatusName?.toLowerCase() === 'connecting';
  const showReconnectingState = isReconnecting || isOnline;
  const showActionButtons = !isReconnecting && isOnline && !isTimedOut;
  const showReconnectInfo = (isReconnecting || isTimedOut) && showPieChart;
  const chartData = isReconnecting
    ? reconnectProgress / 100
    : !isTimedOut
      ? signalLevel / 100
      : 0;
  const signal = isOnline && !isTimedOut ? signalLevel : 0;
  const chartText = isReconnecting ? `${timer}s` : `${signal}%`;

  const capitalize = (str) => {
    if (!str) return 'Offline';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const getStatusText = () => {
    if (isExpired) {
      return expiredAsOffline ? 'Offline' : 'Disabled';
    }
    if (isConnecting || isReconnecting || isTimedOut) {
      return 'Connecting';
    }
    return capitalize(connectionStatusName) || 'Offline';
  };

  const getDeviceStatusName = async () => {
    try {
      const response = await getDeviceStatus();
      if (response?.data?.[deviceSn]) {
        const currentDevice = response?.data?.[deviceSn].device_info;
        return currentDevice?.connection_status_name?.toLowerCase();
      }
    } catch {}
    return null;
  };

  const clearReconnectIntervals = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }
  };

  const resetReconnectState = () => {
    setIsReconnecting(false);
    setTimer(RECONNECT_TIMEOUT);
    setReconnectProgress(0);
  };

  const setDeviceOnline = () => {
    clearReconnectIntervals();
    resetReconnectState();
    setIsTimedOut(false);

    if (refetch) {
      refetch();
    }
  };

  const startReconnecting = () => {
    setIsReconnecting(true);
    setIsTimedOut(false);
    setTimer(RECONNECT_TIMEOUT);
    setReconnectProgress(0);

    timerIntervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearReconnectIntervals();
          setIsReconnecting(false);
          setIsTimedOut(true);
          return RECONNECT_TIMEOUT;
        }
        return prev - 1;
      });
      setReconnectProgress((prev) =>
        Math.min(prev + 100 / RECONNECT_TIMEOUT, 100),
      );
    }, TIMER_INTERVAL);

    checkIntervalRef.current = setInterval(async () => {
      const status = await getDeviceStatusName();
      if (status === 'online') {
        setDeviceOnline();
      }
    }, STATUS_CHECK_INTERVAL);
  };

  useEffect(() => {
    return () => {
      clearReconnectIntervals();
    };
  }, []);

  useEffect(() => {
    clearReconnectIntervals();
    resetReconnectState();
    setIsTimedOut(false);
  }, [deviceSn]);

  const handleRescanNetworks = async () => {
    await deviceSignalScan({
      device_sn: deviceSn,
    });
    startReconnecting();
  };

  const handleRefreshConnection = async () => {
    await deviceRestartConnection({
      device_sn: deviceSn,
    });
    startReconnecting();
  };

  return (
    <div
      className={cn(styles.deviceStatus, className, {
        [styles.deviceStatusOnline]: isOnline && !isExpired,
        [styles.deviceStatusDisabled]: isExpired,
        [styles.deviceStatusConnecting]:
          isConnecting || isReconnecting || isTimedOut,
      })}
    >
      <span>Connection Status</span>
      <div className={styles.deviceStatusContent}>
        <div className={styles.deviceStatusText}>
          <p>{getStatusText()}</p>
        </div>
        {showPieChart && (
          <div>
            {showReconnectingState && (
              <div className={styles.deviceStatusChart}>
                <PieChart
                  id="1"
                  data={chartData}
                  text={<span>{chartText}</span>}
                />
              </div>
            )}
            {showActionButtons && (
              <div className={styles.deviceStatusButtons}>
                <div className={styles.deviceStatusButtonWrapper}>
                  <button
                    className={cn(
                      styles.deviceStatusButton,
                      styles.deviceStatusButtonRescan,
                    )}
                    onClick={handleRescanNetworks}
                  >
                    <RescanIcon />
                    Rescan
                  </button>
                  <span
                    id="rescan-tooltip"
                    role="tooltip"
                    className={styles.deviceStatusTooltip}
                  >
                    {RESCAN_TOOLTIP}
                  </span>
                </div>
                <div className={styles.deviceStatusButtonWrapper}>
                  <button
                    className={cn(
                      styles.deviceStatusButton,
                      styles.deviceStatusButtonRefresh,
                    )}
                    onClick={handleRefreshConnection}
                  >
                    <RefreshIcon />
                    Refresh
                  </button>
                  <span
                    id="refresh-tooltip"
                    role="tooltip"
                    className={styles.deviceStatusTooltip}
                  >
                    {REFRESH_TOOLTIP}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {showReconnectInfo && (
        <div className={styles.deviceStatusReconnect}>
          <div className={styles.deviceStatusReconnectTitle}>
            <AlertIcon />
            Connection will drop.
          </div>
          <div className={styles.deviceStatusReconnectDescription}>
            {!isTimedOut
              ? RECONNECT_DESCRIPTION
              : RECONNECT_TIMEOUT_DESCRIPTION}
          </div>
          {isTimedOut && (
            <button
              className={cn(
                styles.deviceStatusButton,
                styles.deviceStatusButtonRefresh,
              )}
              onClick={() => window.location.reload()}
            >
              <RefreshIcon />
              Refresh
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DeviceStatus;
